import { useTranslation } from 'react-i18next';
import { CalendarClock } from 'lucide-react';
import type { ServiceNotice } from '@gc/shared';

import { useAuth } from './useAuth';

/**
 * The banner's sentence. Zero days left is the venue's last paid day: "ends
 * today". The server also reads a lapsed date as 0 for the hour before the
 * sweep suspends it, so anything below one reads "today" — never "in -1 days".
 */
export function serviceNoticeText(
  t: (key: string, options?: Record<string, unknown>) => string,
  notice: ServiceNotice,
): string {
  return notice.daysLeft <= 0
    ? t('venueService.endsToday')
    : t('venueService.endsIn', { count: notice.daysLeft });
}

/**
 * "Your EclipseGG subscription ends in N days." — on every signed-in page,
 * above the top bar, for as long as `/auth/me` carries a `serviceNotice`
 * (EclipseGG platform, 2026-10-19: a warning before the paid-until date, then
 * the suspension). The server decides whether there is one: it counts in the
 * venue's own time zone, because a till's clock drifts, and sends none for an
 * unbilled venue or the demo. Re-read on every keep-alive renewal, so a screen
 * left open for days still counts down.
 *
 * Not dismissible: it is the one warning before the whole venue stops, and it
 * goes away the moment the seller extends the date.
 */
export function ServiceNoticeBanner() {
  const { t } = useTranslation();
  const { serviceNotice } = useAuth();
  if (!serviceNotice) return null;

  return (
    <div
      role="status"
      data-service-notice
      className="flex shrink-0 items-center gap-2 bg-ending/15 px-4 py-1.5 text-sm text-content"
    >
      <CalendarClock className="h-4 w-4 shrink-0" aria-hidden />
      <span className="min-w-0 flex-1">{serviceNoticeText(t, serviceNotice)}</span>
    </div>
  );
}
